import React, { useState, useEffect } from 'react';
import { Phone, Mail, Menu, X, Home } from 'lucide-react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from './ui/button';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    if (!isHomePage) {
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) element.scrollIntoView({ behavior: 'smooth' });
        setIsMobileMenuOpen(false);
      }, 100);
    } else {
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
        setIsMobileMenuOpen(false);
      }
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md' : 'bg-white/95 backdrop-blur-sm'
      }`}
    >
      <div className="container mx-auto px-4">
        {/* Top Bar */}
        <div className="border-b border-gray-200 py-2 hidden md:block">
          <div className="flex justify-between items-center text-sm">
            <div className="flex items-center gap-6">
              <a href="tel:+919703850009" className="flex items-center gap-2 text-gray-600 hover:text-blue-600 transition-colors">
                <Phone className="w-4 h-4" />
                +91 9703850009
              </a>
              <a href="mailto:prasad@digiloansbazaar.com" className="flex items-center gap-2 text-gray-600 hover:text-blue-600 transition-colors">
                <Mail className="w-4 h-4" />
                prasad@digiloansbazaar.com
              </a>
            </div>
            <div className="text-gray-600">
              Monday – Saturday: 9:00 AM – 7:00 PM
            </div>
          </div>
        </div>

        {/* Main Navigation */}
        <div className="flex justify-between items-center py-4">
          <Link to="/" className="flex items-center gap-3 hover:opacity-90 transition-opacity">
            <img
              src="https://customer-assets.emergentagent.com/job_hospital-loan-hub/artifacts/6e3h6584_1780387421228.png"
              alt="Digi Loans Bazaar Logo"
              className="h-14 w-14 object-contain rounded-lg"
            />
            <div>
              <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-green-600 bg-clip-text text-transparent">
                Digi Loans Bazaar
              </h1>
              <p className="text-xs text-gray-600 italic">Your Dreams, Our Commitment</p>
              <div className="mt-1.5">
                <span className="inline-block bg-gradient-to-r from-blue-700 to-green-600 text-white text-xs sm:text-sm font-bold px-3 py-1 rounded-full shadow-md tracking-wide">
                  UNSECURED LOANS
                </span>
              </div>
            </div>
          </Link>

          {/* Mobile Home Button (visible on mobile only, next to hamburger) */}
          <div className="flex items-center gap-2 lg:hidden">
            {!isHomePage && (
              <Link
                to="/"
                className="flex items-center gap-1 bg-gradient-to-r from-blue-600 to-blue-700 text-white text-xs font-semibold px-3 py-2 rounded-lg shadow-md hover:scale-105 transition-transform"
              >
                <Home className="w-4 h-4" />
                Home
              </Link>
            )}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-2 text-gray-700"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-6">
            <Link
              to="/"
              className={`flex items-center gap-1 font-medium transition-colors ${
                isHomePage ? 'text-blue-600' : 'text-gray-700 hover:text-blue-600'
              }`}
            >
              <Home className="w-4 h-4" />
              Home
            </Link>
            <button onClick={() => scrollToSection('about')} className="text-gray-700 hover:text-blue-600 transition-colors font-medium">
              About
            </button>
            <button onClick={() => scrollToSection('services')} className="text-gray-700 hover:text-blue-600 transition-colors font-medium">
              Services
            </button>
            <Link to="/loan-types" className="text-gray-700 hover:text-blue-600 transition-colors font-medium">
              Loan Types
            </Link>
            <Link to="/faq" className="text-gray-700 hover:text-blue-600 transition-colors font-medium">
              FAQ
            </Link>
            <button onClick={() => scrollToSection('contact')} className="text-gray-700 hover:text-blue-600 transition-colors font-medium">
              Contact
            </button>
            <Button onClick={() => scrollToSection('contact')} className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white">
              Apply Now
            </Button>
          </nav>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <nav className="lg:hidden py-4 border-t border-gray-200">
            <div className="flex flex-col gap-4">
              <Link
                to="/"
                onClick={() => setIsMobileMenuOpen(false)}
                className="flex items-center gap-2 text-gray-700 hover:text-blue-600 transition-colors font-medium py-2"
              >
                <Home className="w-4 h-4" />
                Home
              </Link>
              <button onClick={() => scrollToSection('about')} className="text-left text-gray-700 hover:text-blue-600 transition-colors font-medium py-2">
                About
              </button>
              <button onClick={() => scrollToSection('services')} className="text-left text-gray-700 hover:text-blue-600 transition-colors font-medium py-2">
                Services
              </button>
              <Link
                to="/loan-types"
                onClick={() => setIsMobileMenuOpen(false)}
                className="text-left text-gray-700 hover:text-blue-600 transition-colors font-medium py-2"
              >
                Loan Types
              </Link>
              <Link
                to="/faq"
                onClick={() => setIsMobileMenuOpen(false)}
                className="text-left text-gray-700 hover:text-blue-600 transition-colors font-medium py-2"
              >
                FAQ
              </Link>
              <button onClick={() => scrollToSection('why-choose-us')} className="text-left text-gray-700 hover:text-blue-600 transition-colors font-medium py-2">
                Why Us
              </button>
              <button onClick={() => scrollToSection('founder')} className="text-left text-gray-700 hover:text-blue-600 transition-colors font-medium py-2">
                Founder
              </button>
              <button onClick={() => scrollToSection('contact')} className="text-left text-gray-700 hover:text-blue-600 transition-colors font-medium py-2">
                Contact
              </button>
              <Button onClick={() => scrollToSection('contact')} className="bg-gradient-to-r from-blue-600 to-blue-700 text-white w-full">
                Apply Now
              </Button>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;
